import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  OHIFImagingLabel,
  VisitsWithLabels,
} from '@dynamika-shared/types/imagingLabels';
import { useQCTaskSlice } from '@restore/QCTask/QCTask.slice';
import { useQCTaskSeriesSlice } from '@restore/QCTaskSeries/QCTaskSeries.slice';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { useDXAReadingsSlice } from '@restore/DXAReadings/DXAReadings.slice';
import { useQCTReadingsSlice } from '@restore/QCTReadings/QCTReadings.slice';
import { groupBy } from 'lodash';
import { DICOM_Series } from '@dynamika-shared/types/visits';
import { CommonInventoryType } from '@dynamika-shared/types/inventory';
import { mergeVisitLabel } from './qcLabels.helpers';

export const useLabelsHook = () => {
  const [flatAllVisits, allClinicalTrialLabels] = useClinicalTrialContextSlice(
    s => [s.flatVisits, s.data?.configuration.labelGroups]
  );
  const [taskData, taskDataLoading] = useQCTaskSlice(state => [
    state.data,
    state.loading,
  ]);
  const [series, assignedLabelsByVisit, seriesLoading] = useQCTaskSeriesSlice(
    state => [state.data, state.assignedLabelsByVisit, state.loading]
  );
  const dxaReadings = useDXAReadingsSlice(state => state.visitData?.dxaScores);
  const qctScores = useQCTReadingsSlice(state => state.visitData?.data);

  const isUnscheduled = !taskData?.visitDefinitionId;

  const getMergedLabel = useCallback(
    (
      label: CommonInventoryType,
      assignedLabel?: DICOM_Series
    ): OHIFImagingLabel =>
      mergeVisitLabel(label, assignedLabel, { dxaReadings, qctScores }),
    [dxaReadings, qctScores]
  );

  const unscheduledVisitLabels = useMemo(() => {
    if (isUnscheduled && taskData) {
      const seriesForVisit = series?.find(
        ({ visitId }) => visitId === taskData?.visitId
      );
      const assignedLabels: Record<string, DICOM_Series> = (
        seriesForVisit?.dicomSeries ?? []
      ).reduce((acc: Record<string, DICOM_Series>, label) => {
        return label?.labelId
          ? {
              ...acc,
              [label.labelId]: label,
            }
          : acc;
      }, {});
      const result: OHIFImagingLabel[] = [];
      allClinicalTrialLabels?.forEach(({ labels }) => {
        return labels?.forEach(label => {
          const assignedLabel = assignedLabels?.[label?.id];
          result.push(getMergedLabel(label, assignedLabel));
        });
      });
      return result;
    }
  }, [allClinicalTrialLabels, series, isUnscheduled, taskData, getMergedLabel]);

  const [open, setOpen] = useState<string | boolean | undefined>(taskData?.id);

  useEffect(() => {
    setOpen(taskData?.id);
  }, [taskData?.id]);

  const visitsWithLabels = useMemo(() => {
    return Object.values(flatAllVisits || {}).reduce<VisitsWithLabels>(
      (acc, visit): VisitsWithLabels => {
        return {
          ...acc,
          [visit.id]: {
            visitDefinitionId: visit.id,
            visitName: visit.name,
            labels: Object.values(visit.labels)
              .map(label => {
                const assignedLabel =
                  assignedLabelsByVisit?.[visit?.id]?.[label?.id];
                return getMergedLabel(label, assignedLabel);
              })
              .sort((a, b) => a.name.localeCompare(b.name)),
          },
        };
      },
      {}
    );
  }, [assignedLabelsByVisit, flatAllVisits, getMergedLabel]);

  const currentVisit = visitsWithLabels[taskData?.visitDefinitionId ?? ''];

  const labels = isUnscheduled
    ? unscheduledVisitLabels
    : currentVisit?.labels || [];

  return {
    currentVisitLabels: groupBy(labels, 'dataType'),
    currentVisit,
    visitsWithLabels,
    open,
    setOpen,
    isLoading: taskDataLoading || seriesLoading || !currentVisit,
  };
};
